import { useCallback } from 'react';

import { useDropTodayLessonLinkMutation, usePutTodayLessonLinkMutation } from 'core/graphql';

type TodayLessonLinkType = {
  setTodayLink: (joinUrl: string) => Promise<boolean>;
  clearTodayLink: () => Promise<boolean>;
  setLoading: boolean;
  clearLoading: boolean;
};

// Refetch the single-lesson query so hasJoinLink / linkOverrideActive refresh
// right after the teacher sets or clears the temporary link.
const REFRESH_LESSON_OPTIONS = {
  refetchQueries: ['lessonForLms'],
  awaitRefetchQueries: true,
};

/** Spec 0013: teacher's temporary "today only" lesson link (set / clear). */
export const useTodayLessonLink = (groupId: number): TodayLessonLinkType => {
  const [setLinkMutation, { loading: setLoading }] =
    usePutTodayLessonLinkMutation(REFRESH_LESSON_OPTIONS);
  const [clearLinkMutation, { loading: clearLoading }] =
    useDropTodayLessonLinkMutation(REFRESH_LESSON_OPTIONS);

  const setTodayLink = useCallback(
    async (joinUrl: string) => {
      try {
        const { data } = await setLinkMutation({ variables: { groupId, joinUrl } });
        return Boolean(data?.lmsSetTodayLessonLink?.success);
      } catch {
        return false;
      }
    },
    [setLinkMutation, groupId],
  );

  const clearTodayLink = useCallback(async () => {
    try {
      const { data } = await clearLinkMutation({ variables: { groupId } });
      return Boolean(data?.lmsClearTodayLessonLink?.success);
    } catch {
      return false;
    }
  }, [clearLinkMutation, groupId]);

  return { setTodayLink, clearTodayLink, setLoading, clearLoading };
};
